import type { CSSProperties, FC } from 'react';
import { useMemo, useRef } from 'react';

import { ChipValueFormatter } from '../../../../../formatters/ChipValueFormatter/ChipValueFormatter';
import { ChipTypes } from '../../../../../store/slices/chips/types';
import { useModifiers } from '../../../../../styles/hooks/useModifiers';
import type { ChipViewProps } from '../../types';
import { ChipCalculationHook } from '../lib';

import { ChipDepth } from './ChipDepth';

import styles from './Chip3d.module.css';

interface CustomCSSProps extends CSSProperties {
  '--chipSize': string;
  '--tilt': string;
  '--tiltSin': string;
  '--tiltCos': string;
  '--chipBackground': string;
  '--rotate': string;
  '--rotateX': string;
  '--rotateY': string;
}

const mathPi2 = Math.PI / 2;
const mathPi180 = Math.PI / 180;
export const Chip3d: FC<ChipViewProps> = ({
  color,
  locale,
  size = 100,
  weight,
  value,
  postfix,
  tilt = 0,
  rotate = 0,
  type,
}) => {
  const chipRef = useRef<HTMLDivElement>(null);
  const valueRef = useRef<HTMLDivElement>(null);
  const isFreeChip = type === ChipTypes.FreeChip;

  const mods = useModifiers(
    styles,
    'chip',
    { grow: size === 'grow', freeChipBackground: isFreeChip },
    { withBaseClass: true }
  );

  ChipCalculationHook({ chipRef, valueRef });

  const containerMods = useModifiers(
    styles,
    'chip__container',
    {
      [`weight${weight}`]: true,
    },
    { withBaseClass: true }
  );

  const chipSurfaceMods = useModifiers(styles, 'chip__surface', { freeChip: isFreeChip }, { withBaseClass: true });

  const tiltComponents = useMemo(() => {
    const tiltTransformed = mathPi2 * (1 - tilt);
    const tiltSin = Math.sin(tiltTransformed);
    const tiltCos = Math.cos(tiltTransformed);
    return { tiltSin, tiltCos };
  }, [tilt]);

  const rotateComponents = useMemo(() => {
    const rotationAngle = rotate * mathPi180;
    const rotateX = Math.sin(rotationAngle);
    const rotateY = Math.cos(rotationAngle);
    return { rotateX, rotateY };
  }, [rotate]);

  const computeStyles = useMemo((): CustomCSSProps => {
    const styles = {} as CustomCSSProps;

    if (size !== 'inherit' && size !== 'grow') {
      styles['--chipSize'] = `${size}px`;
    }

    styles['--tiltSin'] = `${tiltComponents.tiltSin}`;
    styles['--tiltCos'] = `${tiltComponents.tiltCos}`;
    styles['--tilt'] = `${tilt}`;
    styles['--rotateX'] = `${rotateComponents.rotateX}`;
    styles['--rotateY'] = `${rotateComponents.rotateY}`;
    styles['--rotate'] = `${rotate}deg`;

    if (color) {
      styles['--chipBackground'] = color;
    }

    return styles;
  }, [
    size,
    tiltComponents.tiltSin,
    tiltComponents.tiltCos,
    tilt,
    rotateComponents.rotateX,
    rotateComponents.rotateY,
    rotate,
    color,
  ]);

  return (
    <span className={containerMods} style={computeStyles}>
      <span className={styles.chip__shadow} />
      {tilt ? <ChipDepth rotate={rotate} tiltComponents={tiltComponents} isFreeChip={isFreeChip} /> : null}
      <span className={chipSurfaceMods} />
      <span className={mods} ref={chipRef}>
        <span className={styles.chip__valueContainer}>
          <span className={styles.chip__value} ref={valueRef}>
            {ChipValueFormatter.format(locale, value)}
            {postfix}
          </span>
        </span>
      </span>
      <span className={styles.chip__glare} />
    </span>
  );
};
